import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { useThemeContext } from '@oberoninternal/travelbase-ds/hooks/useThemeContext';
import { Box, Flex } from '@rebass/grid';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { debounce } from 'debounce';
import { FormikHelpers } from 'formik';
import isEqual from 'lodash.isequal';
import omit from 'lodash.omit';
import { GetServerSidePropsContext, GetStaticPropsContext } from 'next';
import { ParsedUrlQuery } from 'querystring';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { decodeQueryParams } from 'use-query-params';
import { BrandConfig } from '../../constants/brandConfig';
import companySearchParams, {
    companySearchFilterParams,
    CompanySearchParams,
} from '../../constants/companySearchParams';
import { addApolloState, initApolloClient } from '../../createApolloClient';
import { Locale } from '../../entities/Locale';
import {
    CompanySearchDocument,
    CompanySearchParamsInput,
    CompanySearchQuery,
    CompanySearchQueryVariables,
    useCompanyFiltersQuery,
    useCompanySearchQuery,
} from '../../generated/graphql';
import composeFilterCategory from '../../hooks/search/useComposeFilterCategory';
import useFiltersActive from '../../hooks/useFiltersActive';
import useQueryParams from '../../hooks/useQueryParams';
import ContentWrapper, { ContentWrapperVariant } from '../ContentWrapper';
import CompanyTile from '../designsystem/CompanyTile';
import Pagination from '../designsystem/Pagination';
import ViewToggle from '../designsystem/ViewToggle';
import WideGrid, { Cell } from '../designsystem/WideGrid';
import FilterList from '../FilterList';
import Formed from '../Formed';
import ListPaginationLink from '../ListPaginationLink';
import Page from '../Page';
import SearchBottomBar from '../SearchBottomBar';
import SearchFiltersModal from '../SearchFiltersModal';
import SearchFilterTags from '../SearchFilterTags';
import { useTenantContext } from '../../context/TenantContext';
import FavoriteIconButton from '../FavoriteIconButton';
import useGetPageSlugByLocale from '../../hooks/useGetPageSlugByLocale';

export const companiesQuery = gql`
    query CompanySearch($params: CompanySearchParamsInput!, $limit: Int, $offset: Int, $sortByDistanceTo: LatLonInput) {
        companies(params: $params, limit: $limit, offset: $offset, sortByDistanceTo: $sortByDistanceTo) {
            ... on CompanySearchResult {
                totalHits
                hits {
                    ...CompanyHit
                }

                placeCounts {
                    value
                    hits
                }
                themeCounts {
                    value
                    hits
                }
                categoryCounts {
                    value
                    hits
                }
                mainCategoryCounts {
                    value
                    hits
                }
                targetAudienceCount {
                    value
                    hits
                }
            }
        }
    }

    fragment CompanyHit on Company {
        name
        slug
        id
        place
        targetAudiences
        videoUrl
        categories {
            slug
        }
        mainImage {
            transform(config: HERO, format: JPEG) {
                ...ListImage
            }
        }
        webpMainImage: mainImage {
            transform(config: HERO, format: WEBP) {
                ...ListImage
            }
        }
        webpListImage: listImage {
            transform(config: TILE, format: WEBP) {
                ...ListImage
            }
        }
        listImage {
            transform(config: TILE, format: JPEG) {
                ...ListImage
            }
        }
        normalOperatingHours {
            monday {
                timeOpen
                timeClosed
            }
            tuesday {
                timeOpen
                timeClosed
            }
            wednesday {
                timeOpen
                timeClosed
            }
            thursday {
                timeOpen
                timeClosed
            }
            friday {
                timeOpen
                timeClosed
            }
            saturday {
                timeOpen
                timeClosed
            }
            sunday {
                timeOpen
                timeClosed
            }
        }
        specialOperatingHours {
            startDate
            endDate
            monday {
                timeOpen
                timeClosed
            }
            tuesday {
                timeOpen
                timeClosed
            }
            wednesday {
                timeOpen
                timeClosed
            }
            thursday {
                timeOpen
                timeClosed
            }
            friday {
                timeOpen
                timeClosed
            }
            saturday {
                timeOpen
                timeClosed
            }
            sunday {
                timeOpen
                timeClosed
            }
        }
    }

    query CompanyFilters {
        companyCategories {
            name
            slug
            position
            children {
                name
                slug
                position
            }
        }
        places {
            id
            name
        }
    }
`;

export type AllowedSearchAction = 'filters' | 'map' | 'list';

export interface BaseProps {
    bakedInParams?: CompanySearchParams;
    startParams?: CompanySearchParams;
    ssr?: boolean;
}

export interface Props extends BaseProps {
    amountPerPage: number;
    contentWrapperVariant: ContentWrapperVariant;
    buttonTypes: AllowedSearchAction[];
    ssr?: boolean;
    pageTitle?: string;
    hideTitle?: boolean;
    totalHitsResultText?: boolean;
}

export interface CompanySearchValues {
    offset: number;
    categorySlugs?: string[];
    mainCategorySlugs?: string[];
    placeId?: string[];
}

export const useCompanyImageSizes = () => {
    const { mediaQueries } = useThemeContext();
    return `(min-width: ${mediaQueries.xxl}) 25vw, (min-width: ${mediaQueries.xl}) 25vw, (min-width: ${mediaQueries.s}) 25vw, 100vw`;
};

const filterKeys: Array<keyof CompanySearchValues> = ['categorySlugs', 'placeId', 'mainCategorySlugs'];

const Companies: React.FC<React.PropsWithChildren<Props>> = ({
    amountPerPage,
    contentWrapperVariant,
    buttonTypes,
    bakedInParams,
    ssr = false,
    pageTitle,
    hideTitle = false,
    totalHitsResultText = false,
    ...rest
}) => {
    const { colors } = useThemeContext();
    const { brandConfig } = useTenantContext();
    const intl = useIntl();
    const sizes = useCompanyImageSizes();
    const filterModal = useSesame();

    const [params, setParams] = useQueryParams(companySearchParams);
    const [filtersActive, removeActiveFilters] = useFiltersActive(companySearchFilterParams);
    const paramsSameAsBakedIn = useMemo(() => isEqual(params, bakedInParams), [params, bakedInParams]);
    const { getPageSlugByLocale } = useGetPageSlugByLocale();
    const companyPageSlug = getPageSlugByLocale('COMPANY');
    const startParams = params.searched ? {} : rest.startParams;
    const {
        offset = 0,
        categorySlugs = [],
        placeId,
        mainCategorySlugs = [],
    } = {
        ...startParams,
        ...params,
        ...bakedInParams,
    };

    const paramsInput: CompanySearchParamsInput = {
        mainCategorySlugs,
        categorySlugs,
        placeId,
    };

    const filtersQuery = useCompanyFiltersQuery({ ssr });

    const { data, loading } = useCompanySearchQuery({
        variables: {
            offset: offset >= 0 ? offset : 0,
            limit: amountPerPage,
            params: paramsInput,
        },
        ssr,
    });

    const handleSubmit = useCallback(
        (values: CompanySearchValues, helpers: FormikHelpers<CompanySearchValues>) => {
            // by changing the params, apollo will refetch the query automagically
            setParams(
                {
                    categorySlugs: values.categorySlugs ?? [],
                    mainCategorySlugs: values.mainCategorySlugs ?? [],
                    offset: 0,
                    placeId: values.placeId?.[0],
                    searched: true,
                },
                'replaceIn'
            );
            setTimeout(() => helpers.setSubmitting(false));
        },
        [setParams]
    );
    // TODO use useMemo in order te create the function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleAutoSave = useCallback(debounce(handleSubmit), [handleSubmit]);
    const deviceSize = useDeviceSize();
    const shouldAutoSave = deviceSize !== 'mobile';

    const mainCompanyFilterCategory = useMemo(
        () =>
            composeFilterCategory({
                categoryHandle: 'mainCategorySlugs',
                categoryLabel: intl.formatMessage({ defaultMessage: 'Categorie' }),
                filterProperties: [...(filtersQuery.data?.companyCategories ?? [])].sort(
                    (categoryA, categoryB) => categoryA.position - categoryB.position
                ),

                filterCounts: data?.companies.mainCategoryCounts ?? [],
                filterCountKey: 'value',
                filterResolver: ({ slug, name }) => ({ handle: slug, label: name ?? '' }),
            }),
        [data, filtersQuery.data, intl]
    );

    const subCompanyFilterCategory = useMemo(
        () =>
            composeFilterCategory({
                categoryHandle: 'categorySlugs',
                categoryLabel: intl.formatMessage({ defaultMessage: 'Categorie' }),
                filterProperties:
                    mainCategorySlugs.length + categorySlugs.length
                        ? [...(filtersQuery.data?.companyCategories.flatMap(category => category.children) ?? [])].sort(
                              (categoryA, categoryB) => categoryA.position - categoryB.position
                          )
                        : [],
                filterCounts: data?.companies.categoryCounts ?? [],
                filterCountKey: 'value',
                filterResolver: ({ slug, name }) => ({ handle: slug, label: name ?? '' }),
            }),
        [categorySlugs.length, data, filtersQuery.data, intl, mainCategorySlugs.length]
    );

    const placesFilterCategory = useMemo(
        () =>
            composeFilterCategory({
                categoryHandle: 'placeId',
                categoryLabel: intl.formatMessage({ defaultMessage: 'Plaats' }),
                filterProperties: filtersQuery.data?.places ?? [],
                filterCounts: data?.companies.placeCounts ?? [],
                filterCountKey: 'value',
                filterResolver: ({ id, name }) => ({ handle: id, label: name }),
            }),
        [data, filtersQuery.data, intl]
    );

    const allFilterCategories = useMemo(
        () => [mainCompanyFilterCategory, subCompanyFilterCategory, placesFilterCategory],
        [mainCompanyFilterCategory, placesFilterCategory, subCompanyFilterCategory]
    );
    // when a mainCategory is selected it's hidden from the UI
    const filterCategories = allFilterCategories.filter(
        category => category.handle !== 'mainCategorySlugs' || !mainCategorySlugs.length
    );

    const { totalHits = 0 } = data?.companies ?? {};
    const pageCount = Math.ceil(totalHits / amountPerPage);
    const currentPageIndex = Math.floor(offset / amountPerPage);

    return (
        <Page id="search-page">
            <Formed<CompanySearchValues>
                skipPrompt
                initialValues={{
                    offset,
                    mainCategorySlugs,
                    categorySlugs,
                    placeId: placeId ? [placeId] : [],
                }}
                handleAutoSave={
                    shouldAutoSave
                        ? (values, helpers) => {
                              helpers.setSubmitting(true);
                              handleAutoSave(values, helpers);
                          }
                        : undefined
                }
                handleSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isSubmitting }) => {
                    const isLoading = loading || isSubmitting;
                    return (
                        <StyledContentWrapper variant={contentWrapperVariant}>
                            <Flex flexDirection={['column', null, null, 'row']}>
                                {/* hide the whole bar on small devices when the map is active */}
                                <LeftBar className={contentWrapperVariant === 'searchMap' ? 'gt-m' : undefined}>
                                    <SectionHeader className="gt-m" hideTitle={hideTitle}>
                                        {loading ? (
                                            <Skeleton height="1em" width={250} />
                                        ) : (
                                            <Title elementType="h2" style={{ fontWeight: 400 }}>
                                                {data?.companies.totalHits === 1 ? (
                                                    <>
                                                        <span style={{ color: colors.secondary['40'] }}>
                                                            {data?.companies.totalHits}
                                                        </span>{' '}
                                                        {totalHitsResultText ? (
                                                            <FormattedMessage defaultMessage="resultaat" />
                                                        ) : (
                                                            <FormattedMessage defaultMessage="bedrijf" />
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <span style={{ color: colors.secondary['40'] }}>
                                                            {data?.companies.totalHits}
                                                        </span>{' '}
                                                        {pageTitle ??
                                                            (totalHitsResultText ? (
                                                                <FormattedMessage defaultMessage="resultaten" />
                                                            ) : (
                                                                <FormattedMessage defaultMessage="bedrijven" />
                                                            ))}
                                                    </>
                                                )}
                                            </Title>
                                        )}
                                    </SectionHeader>

                                    <SearchFilterTags
                                        shouldAutoSave={shouldAutoSave}
                                        categories={allFilterCategories}
                                    />
                                    {filtersQuery.loading && <FilterList loading />}
                                    {!filtersQuery.loading && (
                                        <FilterList categories={filterCategories} className="gt-m" />
                                    )}
                                </LeftBar>

                                <SearchBottomBar
                                    allowedActions={buttonTypes}
                                    filtersActive={filtersActive}
                                    onFiltersClick={filterModal.onOpen}
                                />
                                <SearchFiltersModal
                                    dirtyCheckKeys={filterKeys}
                                    filtersActive={filtersActive}
                                    onClose={filterModal.onClose}
                                    open={filterModal.open}
                                    removeActiveFilters={removeActiveFilters}
                                >
                                    <FilterList categories={filterCategories} />
                                </SearchFiltersModal>

                                <ListingSection>
                                    {!hideTitle && (
                                        <SectionHeader className="gt-m">
                                            <Title>
                                                {!isLoading &&
                                                data?.companies.hits &&
                                                data?.companies.hits.length === 0 ? (
                                                    <FormattedMessage
                                                        defaultMessage="Geen {pageTitle} gevonden"
                                                        values={{
                                                            pageTitle:
                                                                pageTitle ??
                                                                intl.formatMessage({
                                                                    defaultMessage: 'bedrijven',
                                                                }),
                                                        }}
                                                    />
                                                ) : (
                                                    <FormattedMessage
                                                        defaultMessage="{pageTitle} overzicht"
                                                        values={{
                                                            pageTitle: pageTitle
                                                                ? pageTitle.charAt(0).toUpperCase() + pageTitle.slice(1)
                                                                : intl.formatMessage({
                                                                      defaultMessage: 'bedrijven',
                                                                  }),
                                                        }}
                                                    />
                                                )}
                                            </Title>

                                            {/* Add this back once map view is implemented */}
                                            {/* <StyledViewToggle allowMap allowList={false} /> */}
                                        </SectionHeader>
                                    )}
                                    <Body
                                        className="lt-m"
                                        variant="large"
                                        style={{ fontWeight: 400, marginTop: '2.4rem' }}
                                    >
                                        {data?.companies.totalHits === 1 ? (
                                            <>
                                                <span style={{ color: colors.secondary['40'] }}>
                                                    {data?.companies.totalHits}
                                                </span>{' '}
                                                <FormattedMessage defaultMessage="Bedrijf" />
                                            </>
                                        ) : (
                                            <>
                                                <span style={{ color: colors.secondary['40'] }}>
                                                    {data?.companies.totalHits}
                                                </span>{' '}
                                                <FormattedMessage defaultMessage="Bedrijven" />
                                            </>
                                        )}
                                    </Body>
                                    {!isLoading &&
                                        data?.companies.hits &&
                                        data?.companies.hits.length === 0 &&
                                        filtersActive &&
                                        !paramsSameAsBakedIn && (
                                            <Button
                                                style={{
                                                    alignSelf: 'flex-start',
                                                }}
                                                variant="outline"
                                                onClick={removeActiveFilters}
                                                type="button"
                                            >
                                                <FormattedMessage defaultMessage="Filters verwijderen" />
                                            </Button>
                                        )}
                                    <WideGrid widths={[100, 50, 100 / 3]}>
                                        {isLoading &&
                                            new Array(amountPerPage).fill(null).map((_, i) => (
                                                <Cell key={i}>
                                                    <CompanyTile key={i} loading />
                                                </Cell>
                                            ))}
                                        {!isLoading &&
                                            data?.companies.hits &&
                                            data?.companies.hits.length > 0 &&
                                            data?.companies.hits.map(company => (
                                                <Cell key={company.slug}>
                                                    <CompanyTile
                                                        sources={[
                                                            {
                                                                srcSet:
                                                                    company.webpListImage?.transform?.srcSet ??
                                                                    company.webpMainImage?.transform?.srcSet,
                                                                type: 'image/webp',
                                                                sizes,
                                                            },
                                                            {
                                                                srcSet:
                                                                    company.listImage?.transform?.srcSet ??
                                                                    company.mainImage?.transform?.srcSet,
                                                                type: 'image/jpeg',
                                                                sizes,
                                                            },
                                                        ]}
                                                        fallback={{
                                                            src:
                                                                company.listImage?.transform?.src ??
                                                                company.mainImage?.transform?.src,
                                                        }}
                                                        icon={
                                                            brandConfig.enableFavorites && (
                                                                <FavoriteIconButton type="company" id={company.id} />
                                                            )
                                                        }
                                                        videoUrl={company.videoUrl}
                                                        name={company.name}
                                                        place={company.place}
                                                        loading={isLoading}
                                                        linkProps={{
                                                            href: {
                                                                pathname: `/${companyPageSlug}/${company.slug}`,
                                                            },
                                                            locale: intl.locale,
                                                        }}
                                                    />
                                                </Cell>
                                            ))}
                                    </WideGrid>
                                    <StyledPagination
                                        currentPageIndex={currentPageIndex}
                                        pagesCount={pageCount}
                                        LinkComponent={ListPaginationLink}
                                    />
                                </ListingSection>
                            </Flex>
                        </StyledContentWrapper>
                    );
                }}
            </Formed>
        </Page>
    );
};

export default Companies;

interface Options {
    amountsPerPage?: number;
    pathname?: string;
    startParams?: CompanySearchParams;
    bakedInParams?: CompanySearchParams;
    client?: ApolloClient<NormalizedCacheObject>;
    brandConfig: BrandConfig;
}

export const createGetServerSideProps =
    <Q extends ParsedUrlQuery>({
        amountsPerPage,
        startParams = {},
        bakedInParams = {},
        client: existingClient,
    }: Options) =>
    async (context: GetStaticPropsContext<Q> | GetServerSidePropsContext<Q>) => {
        const query: ParsedUrlQuery | undefined = 'query' in context ? context.query : context.params;

        const decodedParams = decodeQueryParams(
            companySearchParams,
            omit(query, ['locale', ...Object.keys(bakedInParams)])
        );

        const queryParams = {
            ...(decodedParams.searched ? {} : startParams),
            ...decodedParams,
            ...bakedInParams,
        };

        const { categorySlugs, placeId, offset = 0 } = queryParams;

        const paramsInput: CompanySearchParamsInput = {
            categorySlugs,
            placeId,
        };

        const client = existingClient ?? initApolloClient(context.locale as Locale);

        await client.query<CompanySearchQuery, CompanySearchQueryVariables>({
            query: CompanySearchDocument,
            variables: {
                // some protection as an offset < 0 will let the BE explode 🧨
                offset: offset >= 0 ? offset : 0,
                limit: amountsPerPage,
                params: paramsInput,
            },
        });

        return addApolloState(client, { props: { bakedInParams, startParams } });
    };

const StyledContentWrapper = styled(ContentWrapper)`
    padding-bottom: 4rem;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        padding-bottom: 8rem;
    }
`;

const StyledViewToggle = styled(ViewToggle)``;

const SectionHeader = styled.header<{ hideTitle?: boolean }>`
    display: flex;
    grid-column: 1 / -1;
    margin: ${({ hideTitle }) => (hideTitle ? '0 0 4rem 0' : '4rem 0')};
    align-items: center;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xs}) {
        margin: ${({ hideTitle }) => (hideTitle ? '0 0 4rem 0' : '4rem 0')};
        height: 4.8rem;

        ${StyledViewToggle} {
            margin-left: auto;
        }
    }
`;

const LeftBar = styled(Box)`
    flex-shrink: 0;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 29.6rem;
        padding-right: ${({ theme }) => theme.spacing['60_Large']};

        ${SectionHeader} {
            margin-bottom: 2.4rem;
        }
    }
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xl}) {
        width: 32.8rem;
    }
`;

const ListingSection = styled.section`
    width: 100%;
`;

const StyledPagination = styled(Pagination)`
    margin-top: 4rem;
`;
